import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import avatar from '../assets/brand/maklerpass-icon.svg';
import { Icon } from '../components/icons';
import { MutationError } from '../components/MutationError';
import { tourEvent } from '../onboarding/tourSteps';
import type { AssistantVoiceStatus } from '../api/types';
import { useProject } from '../projects/context';
import { answerBlocks, inlineParts } from './format';
import { type PageContext, suggestionsFor } from './pageContext';
import { hintsEnabled, setHintsEnabled } from './proactive';
import type { AssistantChat } from './useAssistantChat';

// Gespräch mit der Assistentin (ElevenLabs) erst beim ersten Sprechen laden – das SDK bleibt aus dem Hauptbündel.
const VoiceChat = lazy(() => import('./VoiceChat'));

// KI-Hilfe (Dominik 29.09.2026, Chat-Box seit 16.10.2026): die KI erklärt anhand des Handbuchs, was wie wo zu tun ist – passend
// zur geöffneten Seite, schriftlich oder im Gespräch. Kundendaten werden nicht mitgeschickt, der Verlauf bleibt im Browser.

function AnswerText({ text }: { text: string }) {
  const inline = (s: string) => inlineParts(s).map((p, i) => (p.strong ? <strong key={i}>{p.text}</strong> : <span key={i}>{p.text}</span>));
  return (
    <>
      {answerBlocks(text).map((b, i) =>
        b.kind === 'p' ? (
          <p key={i}>{inline(b.text)}</p>
        ) : b.kind === 'h' ? (
          <p key={i} className="assistant-answer-heading">
            {inline(b.text)}
          </p>
        ) : b.kind === 'ol' ? (
          <ol key={i}>
            {b.items.map((item, j) => (
              <li key={j}>{inline(item)}</li>
            ))}
          </ol>
        ) : (
          <ul key={i}>
            {b.items.map((item, j) => (
              <li key={j}>{inline(item)}</li>
            ))}
          </ul>
        ),
      )}
    </>
  );
}

// AssistantToggle: Schaltfläche in der Kopfleiste (öffnet und schließt die Chat-Box).
export function AssistantToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="icon-btn assistant-toggle" aria-label="KI-Hilfe" aria-expanded={open} onClick={onToggle}>
      <Icon name="sparkle" size={20} />
      <span className="assistant-toggle-label">KI-Hilfe</span>
    </button>
  );
}

interface PanelProps {
  open: boolean;
  onClose: () => void;
  page: PageContext;
  chat: AssistantChat;
  voice: AssistantVoiceStatus | undefined;
  voiceMode: boolean;
  onVoiceMode: (on: boolean) => void;
}

export function AssistantPanel({ open, onClose, page, chat, voice: voiceStatus, voiceMode, onVoiceMode }: PanelProps) {
  const { project } = useProject();
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [hints, setHints] = useState(hintsEnabled);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const voiceOn = !!voiceStatus?.enabled;
  useEffect(() => {
    if (open && !voiceMode) inputRef.current?.focus();
  }, [open, voiceMode]);
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [chat.messages.length, chat.pending]);

  const submit = () => {
    if (!draft.trim() || chat.pending) return;
    chat.send(draft);
    setDraft('');
  };

  return (
    <aside className="assistant-panel" aria-label="KI-Hilfe" hidden={!open} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <header className="assistant-head">
        <img className="assistant-avatar" src={avatar} alt="" />
        <div className="assistant-head-title">
          <strong>Maklerpass-Assistentin</strong>
          <span>Seite: {page.label}</span>
        </div>
        <div className="assistant-head-actions">
          {voiceOn && (
            <button
              type="button"
              className={voiceMode ? 'assistant-head-btn pill active' : 'assistant-head-btn pill'}
              aria-pressed={voiceMode}
              aria-label="Mit der Assistentin sprechen"
              title="Mit der Assistentin sprechen"
              onClick={() => onVoiceMode(!voiceMode)}
            >
              <Icon name="mic" size={16} />
              <span>Sprechen</span>
            </button>
          )}
          {chat.messages.length > 0 && (
            <button type="button" className="assistant-head-btn text" onClick={chat.reset}>
              Neuer Chat
            </button>
          )}
          <button type="button" className="assistant-head-btn" aria-label="KI-Hilfe schließen" title="Schließen" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </div>
      </header>
      <div className="assistant-body" role="log" aria-live="polite">
        {chat.messages.length === 0 && (
          <div className="assistant-intro">
            <p className="assistant-intro-title">Hallo! Wobei kann ich helfen?</p>
            <p>
              Ich erkläre Schritt für Schritt, wie etwas in Maklerpass geht – passend zur geöffneten Seite.{' '}
              <button type="button" className="link-button" onClick={() => window.dispatchEvent(new Event(tourEvent))}>
                Einführung ansehen
              </button>
            </p>
            {voiceOn && !voiceMode && (
              <button type="button" className="assistant-voice-cta" onClick={() => onVoiceMode(true)}>
                <span className="assistant-voice-cta-icon" aria-hidden="true">
                  <Icon name="mic" size={20} />
                </span>
                <span>
                  <strong>Lieber sprechen?</strong>
                  <span>Gespräch mit der Assistentin starten</span>
                </span>
              </button>
            )}
            <div className="assistant-suggestions" role="group" aria-label="Vorschläge">
              {suggestionsFor(page.key).map((q) => (
                <button key={q} type="button" className="chip" onClick={() => chat.send(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {chat.messages.map((m, i) => (
          <div key={i} className={`assistant-row ${m.role}`}>
            {m.role === 'assistant' && <img className="assistant-avatar small" src={avatar} alt="" />}
            <div className={`assistant-msg ${m.role}`}>
              {m.voice && (
                <span className="assistant-voice-mark">
                  <Icon name="mic" size={12} /> gesprochen
                </span>
              )}
              {m.role === 'assistant' ? <AnswerText text={m.content} /> : <p>{m.content}</p>}
              {!!m.links?.length && (
                <div className="assistant-links">
                  {m.links.map((l) => (
                    <button key={l.path} type="button" className="btn btn-small" onClick={() => navigate(`/p/${project.key}/${l.path}`)}>
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {chat.pending && (
          <div className="assistant-row assistant">
            <img className="assistant-avatar small" src={avatar} alt="" />
            <p className="assistant-msg assistant assistant-typing" role="status" aria-label="KI-Hilfe schreibt …">
              <span />
              <span />
              <span />
            </p>
          </div>
        )}
        {chat.error && <MutationError error={chat.error} />}
        <div ref={endRef} />
      </div>
      {voiceMode && voiceStatus?.enabled ? (
        <Suspense fallback={<p className="assistant-voice-loading muted">Gespräch wird vorbereitet …</p>}>
          <VoiceChat
            page={page}
            remaining={voiceStatus.remaining}
            maxSeconds={voiceStatus.max_seconds}
            onTranscript={chat.addVoice}
            onClose={() => onVoiceMode(false)}
          />
        </Suspense>
      ) : (
        <form
          className="assistant-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <textarea
            ref={inputRef}
            aria-label="Frage an die KI-Hilfe"
            rows={2}
            maxLength={1000}
            value={draft}
            placeholder={voiceOn ? 'Frage tippen – oder Mikrofon antippen und sprechen' : 'Ihre Frage, z. B. Wie buche ich einen Termin?'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          {voiceOn && (
            <button type="button" className="btn assistant-mic" aria-label="Sprechen statt tippen" title="Sprechen statt tippen" onClick={() => onVoiceMode(true)}>
              <Icon name="mic" size={18} />
            </button>
          )}
          <button type="submit" className="btn btn-primary assistant-send" aria-label="Senden" disabled={!draft.trim() || chat.pending}>
            <Icon name="send" size={18} />
          </button>
        </form>
      )}
      <div className="assistant-note">
        <Icon name="lock" size={14} />
        <span>Kennt das Handbuch und die geöffnete Seite – keine Kundendaten. Bitte keine personenbezogenen Daten eingeben.</span>
        <button
          type="button"
          className="link-button"
          aria-pressed={hints}
          onClick={() => {
            setHintsEnabled(!hints);
            setHints(!hints);
          }}
        >
          {hints ? 'Hinweise aus' : 'Hinweise an'}
        </button>
      </div>
    </aside>
  );
}
